import { hasLapChildData } from './lapChild';

/**
 * Bus categories that count as a free fare (as opposed to `general` /
 * `oversold` adult tickets). Anything in this set means the passenger is
 * traveling under a regulated discount that the operator must validate at
 * the physical counter before issuing the boarding ticket.
 */
export const FREE_FARE_CATEGORIES = [
  'older',
  'older_half',
  'free_pass',
  'intermunicipal_free_pass',
  'disadvantaged_youth',
  'disadvantaged_youth_half',
];

/**
 * Whether a single bus_category value is a free fare.
 * @param {string} busCategory - The category to check.
 * @returns {boolean} True if the category is a free fare.
 */
export const isFreeFareCategory = (busCategory) => FREE_FARE_CATEGORIES.includes(busCategory);

/** Gratuity categories that may appear on API tickets alongside {@link FREE_FARE_CATEGORIES}. */
const EXTRA_FREE_FARE_TICKET_CATEGORIES = ['disability', 'special', 'wheelchair_handicap'];

/**
 * Whether a ticket `category` value counts as free fare for “all gratuity” purchase-complete UI.
 * @param {string|undefined|null} category - Ticket category from fragments.
 * @returns {boolean}
 */
export const isFreeFareTicketCategory = (category) =>
  Boolean(category) &&
  (isFreeFareCategory(category) || EXTRA_FREE_FARE_TICKET_CATEGORIES.includes(category));

/**
 * True when every fragment ticket is a free-fare category (no paid adult seats in the booking).
 * Used to hide digital ticket download UI on Purchase complete; callers should treat no tickets as false.
 * @param {object} purchase - Purchase object (toJS shape from redux).
 * @returns {boolean}
 */
export const purchaseIsAllFreeFareTickets = (purchase) => {
  if (!purchase) return false;

  const fragments = [
    ...((purchase.departs && purchase.departs.fragments) || []),
    ...((purchase.returns && purchase.returns.fragments) || []),
  ];
  const tickets = fragments.flatMap((fragment) => (fragment && fragment.tickets) || []);

  if (tickets.length === 0) return false;

  return tickets.every((ticket) => isFreeFareTicketCategory(ticket && ticket.category));
};

/**
 * Whether the purchase carries at least one passenger who travels under a
 * free fare. Walks four sources so the answer stays correct across the full
 * lifecycle: `passengers[].meta.lapChild` (lap children share an adult's
 * seat and travel free), ticket `category` after confirma-venda, the
 * `passengerSelection` count map once it has been dispatched, then the
 * `passengers[].busCategory` array which is populated during checkout
 * before either of the ticket / selection sources land.
 * @param {object} purchase - The purchase object (toJS shape from redux).
 * @returns {boolean} True if at least one passenger travels free (regulated discount or lap child).
 */
export const purchaseHasFreeFarePassenger = (purchase) => {
  if (!purchase) return false;

  const passengers = purchase.passengers || [];
  const hasLapChild = passengers.some((passenger) => hasLapChildData(passenger?.meta?.lapChild));
  if (hasLapChild) return true;

  const fragments = [
    ...((purchase.departs && purchase.departs.fragments) || []),
    ...((purchase.returns && purchase.returns.fragments) || []),
  ];
  const tickets = fragments.flatMap((fragment) => (fragment && fragment.tickets) || []);

  if (tickets.length > 0) {
    return tickets.some((ticket) => isFreeFareCategory(ticket && ticket.category));
  }

  const selection = purchase.passengerSelection || {};
  const selectionKeys = Object.keys(selection);
  if (selectionKeys.length > 0) {
    return selectionKeys.some(
      (category) => Number(selection[category]) > 0 && isFreeFareCategory(category),
    );
  }

  return passengers.some((passenger) => passenger && isFreeFareCategory(passenger.busCategory));
};
